import { useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import type { WorldCupMatch } from '../types/worldcup.types';
import { isCorrectOutcome } from '../helpers/worldcup.helper';
import {
  getSeenPostGameMatchIds,
  markPostGameMatchesSeen,
} from '../helpers/postGameResultSeen.helper';
import { shouldShowReviewPrompt } from '../helpers/reviewPromptTrigger.helper';
import {
  hasSeenReviewPrompt,
  markReviewPromptSeen,
} from '../../shared/helpers/reviewPromptSeen.helper';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export interface PostGameResultMatch {
  match: WorldCupMatch;
  isExactHit: boolean;
  isCorrectResult: boolean;
  earnedPoints: number;
  isBoosted: boolean;
}

interface UsePostGameResultOverlayProps {
  matches: WorldCupMatch[];
  userId: string | undefined;
  isDataReady: boolean;
}

export function usePostGameResultOverlay({
  matches,
  userId,
  isDataReady,
}: UsePostGameResultOverlayProps) {
  const [queue, setQueue] = useState<PostGameResultMatch[]>([]);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isReviewPromptVisible, setIsReviewPromptVisible] = useState(false);
  const hasInitialised = useRef(false);

  useEffect(() => {
    if (!isDataReady || !userId || hasInitialised.current) return;

    async function buildQueue() {
      hasInitialised.current = true;
      const seen = await getSeenPostGameMatchIds(userId!);
      const now = Date.now();

      const staleIds: string[] = [];
      const candidates: PostGameResultMatch[] = [];

      for (const match of matches) {
        if (match.status !== 'finished') continue;

        const kickoff = new Date(match.kickoffTime).getTime();
        const isRecent = now - kickoff <= TWENTY_FOUR_HOURS_MS;

        if (!isRecent) {
          // Silently seed old matches as seen so they never surface later.
          if (!seen.has(match.id)) staleIds.push(match.id);
          continue;
        }

        if (seen.has(match.id)) continue;

        const prediction = match.prediction;
        const hasPrediction =
          prediction != null &&
          prediction.home != null &&
          prediction.away != null;
        if (!hasPrediction) continue;

        // Only show once scoring is complete (pointsEarned is null while pending).
        if (prediction!.pointsEarned == null) continue;

        // Use final scores (homeScore/awayScore) for outcome comparison — not 90-min scores.
        // For ET/penalty matches the user picks the overall winner, so we must compare against
        // the final result (e.g. Belgium 3-2 after ET, not the 90-min 2-2).
        const scoredHome = match.homeScore;
        const scoredAway = match.awayScore;
        if (scoredHome == null || scoredAway == null) continue;

        const isExactHit =
          prediction!.home === scoredHome && prediction!.away === scoredAway;
        const isCorrectResult =
          !isExactHit &&
          isCorrectOutcome(scoredHome, scoredAway, prediction!.home, prediction!.away);

        candidates.push({
          match,
          isExactHit,
          isCorrectResult,
          earnedPoints: prediction!.pointsEarned ?? 0,
          isBoosted: prediction!.isBoosted ?? false,
        });
      }

      if (staleIds.length > 0) {
        await markPostGameMatchesSeen(userId!, staleIds);
      }

      // Most recent kickoff first.
      candidates.sort(
        (a, b) =>
          new Date(b.match.kickoffTime).getTime() -
          new Date(a.match.kickoffTime).getTime(),
      );

      if (candidates.length > 0) {
        setQueue(candidates);
        setIsOverlayVisible(true);
      }
    }

    buildQueue();
  }, [isDataReady, userId, matches]);

  const dismissCurrent = useCallback(async () => {
    const current = queue[0];
    if (!current || !userId) return;

    await markPostGameMatchesSeen(userId, [current.match.id]);
    const next = queue.slice(1);
    setQueue(next);
    if (next.length === 0) {
      setIsOverlayVisible(false);

      // One-time review prompt on a winning last result (specs/review-prompt).
      const showReviewPrompt = await shouldShowReviewPrompt({
        platformOS: Platform.OS,
        isWin: current.isExactHit || current.isCorrectResult,
        queueEmptyAfterDismiss: true,
        hasSeen: () => hasSeenReviewPrompt(userId),
      });
      if (showReviewPrompt) {
        // Marked on show, before any button press — once ever, whatever they tap.
        await markReviewPromptSeen(userId);
        setIsReviewPromptVisible(true);
      }
    }
  }, [queue, userId]);

  const closeReviewPrompt = useCallback(() => {
    setIsReviewPromptVisible(false);
  }, []);

  return {
    isOverlayVisible,
    currentResult: queue[0] ?? null,
    dismissCurrent,
    isReviewPromptVisible,
    closeReviewPrompt,
  };
}
